import { useCallback } from 'react';

import { useQueriesControl } from './url';

const DialogRegistry = {
	redemption: {
		params: ['rewardId'],
	},
	reward: {
		params: ['rewardId'],
	},
} as const;

type DialogRegistry = typeof DialogRegistry;
type DialogId = keyof DialogRegistry;
type DialogParams<T extends DialogId> = DialogRegistry[T]['params'];
type DialogParamsObject<T extends DialogId> = {
	[K in DialogParams<T>[number]]: string | null;
};

export const DIALOG_SEARCH_PARAM = 'dialog';

export const useDialogControl = <T extends DialogId>(id: T) => {
	const params = DialogRegistry[id].params ?? [];
	const [queries, setQueries] = useQueriesControl([DIALOG_SEARCH_PARAM, ...params]);

	// TODO: We should optimize useQueriesControl to use useCallback
	const onOpen = useCallback(
		(params: DialogParamsObject<T>) => {
			setQueries({
				[DIALOG_SEARCH_PARAM]: id,
				...params,
			});
		},
		[id, setQueries]
	);

	const onClose = useCallback(() => {
		const params: Record<string, null> = {
			[DIALOG_SEARCH_PARAM]: null,
		};

		DialogRegistry[id].params?.forEach((param) => {
			params[param] = null;
		});

		setQueries(params);
	}, [id, setQueries]);

	return {
		isOpen: queries[DIALOG_SEARCH_PARAM] === id,
		onOpen,
		onClose,
		params: queries,
	};
};

export const parseDialogParams = <T extends DialogId>(searchParams: URLSearchParams, id: T) => {
	const isOpen = searchParams.get(DIALOG_SEARCH_PARAM) === id;

	const params: Partial<DialogParamsObject<T>> = {};

	if (isOpen) {
		DialogRegistry[id].params.forEach((param) => {
			const value = searchParams.get(param);
			(params as any)[param] = value;
		});
	}

	return {
		isOpen,
		params: params as DialogParamsObject<T>,
	};
};
